import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory, useParams } from 'react-router-dom';
import { updateProfile, updateProfiles } from 'flow/actions/profile-actions';
import SettingSection from 'flow/components/Setting/SettingSection';
import Button from 'flow/components/ui-components/Button';
import Divider from 'flow/components/ui-components/Divider';
import Loading from 'flow/components/ui-components/Loading';
import SettingSelectableItem from 'flow/components/ui-components/SettingSelectableItem';
import useAppContext from 'flow/hooks/useAppContext';
import useContact from 'flow/hooks/useContact';
import useProfiles from 'flow/hooks/useProfiles';
import { routes } from 'flow/types/routes';

import { NumberState } from 'flow/utils/extension-utils';
import { updateSMSSenderName } from 'flow/utils/profile-utils';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { generatePath } from 'flow/utils/router-utils';

interface Props {
    handleVerifyNumber: () => void;
}

const SettingsCalleridSMSEdit = ({ handleVerifyNumber }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const { extensionKey, profileKey } = useParams<{
        extensionKey: ExtensionKey;
        profileKey?: string;
    }>();
    const { contact } = useContact(extensionKey);
    const { profiles, getProfile } = useProfiles(extensionKey);

    const smsSenderNameOptions = useAppSelector((state) =>
        state.profiles.get('smsSenderNameOptions'),
    );
    const mobileStatus = useAppSelector((state) =>
        state.extensions.getIn(['extensions', extensionKey, 'mobileStatus']),
    );

    const handleBack = () => {
        if (profileKey) {
            history.push(
                generatePath(routes['settings.callerid.profile.sms'], {
                    extensionKey,
                }),
            );
        } else {
            history.push(
                generatePath(routes['settings.callerid'], { extensionKey }),
            );
        }
    };

    const handleSelect = (key: string) => {
        if (profileKey) {
            const profile = getProfile(profileKey);

            if (profile) {
                const updatedProfile = updateSMSSenderName(profile, key);
                dispatch(updateProfile(extensionKey, updatedProfile));
            }
        } else {
            const updatedProfileList = profiles.map((profile) =>
                updateSMSSenderName(profile, key),
            );
            dispatch(updateProfiles(extensionKey, updatedProfileList));
        }

        handleBack();
    };

    if (!profiles || !smsSenderNameOptions || !mobileStatus || !contact) {
        return <Loading />;
    }

    const profile = profileKey ? getProfile(profileKey) : profiles.first();

    const getMobileStatus = () => {
        const numberStatus = (() => {
            switch (mobileStatus.get('mobileNumberState')) {
                case NumberState.nonverified.key:
                    return (
                        <div className="flex items-center gap-2">
                            {i18next.t(
                                'settings.callerid.sms.edit.nonverified',
                            )}
                            <Button
                                onClick={handleVerifyNumber}
                                color="primary"
                                className="inline-block"
                            >
                                {i18next.t('settings.contact.verify.number')}
                            </Button>
                        </div>
                    );
                case NumberState.numbermissing.key:
                    return i18next.t(
                        'settings.callerid.sms.edit.numbermissing',
                    );
                default:
                    return null;
            }
        })();

        if (numberStatus) {
            return <SettingSection>{numberStatus}</SettingSection>;
        }

        return null;
    };

    const sortedList = smsSenderNameOptions
        .sortBy(
            (value, key) => key,
            (optionA, optionB) => (optionA < optionB ? -1 : 1),
        )
        .valueSeq();

    return (
        <div>
            {getMobileStatus()}

            <Divider />

            {sortedList.map((smsSenderNameId) => (
                <SettingSelectableItem
                    key={smsSenderNameId.get('key')}
                    value={smsSenderNameId.get('key')}
                    onClick={handleSelect}
                    selected={profile.getIn(['smsSenderNameId', 'key'])}
                >
                    {smsSenderNameId.get('description')}
                </SettingSelectableItem>
            ))}

            {mobileStatus.get('mobileNumberState') ===
                NumberState.nonverified.key && (
                <SettingSelectableItem
                    value={mobileStatus.getIn([
                        'number',
                        'prettyNationalNumber',
                    ])}
                    onClick={handleSelect}
                    selected={'false'}
                >
                    {mobileStatus.getIn(['number', 'prettyNationalNumber'])}
                </SettingSelectableItem>
            )}

            <Divider />
        </div>
    );
};

export default SettingsCalleridSMSEdit;
